import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Crown, UserPlus, User, Home, X, Trash2, Building2, Printer, FileDown } from "lucide-react";
import { api, errMsg, API, getToken } from "../lib/api";
import { namaGelar } from "../lib/utils";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import SearchableSelect from "../components/SearchableSelect";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { Breadcrumb } from "../components/Breadcrumb";
import { KartuKeluargaPrint } from "../components/KartuKeluargaPrint";
import { SealamatTanpaKK } from "../components/SealamatTanpaKK";
import { AnggotaNonJemaat } from "../components/AnggotaNonJemaat";

export default function KeluargaDetailPage() {
  const navigate = useNavigate();
  const canWrite = useAuth().can("keluarga:write");
  const [nonJemaat, setNonJemaat] = useState([]);
  const canDelete = useAuth().can("keluarga:delete");
  const [delOpen, setDelOpen] = useState(false);
  const doDelete = async () => { setSaving(true); try { await api.delete(`/keluarga/${id}`); toast.success("Keluarga dihapus"); navigate("/keluarga"); } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); } };
  const { id } = useParams();
  const [fam, setFam] = useState(null);
  const [master, setMaster] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [pickJemaat, setPickJemaat] = useState("");
  const [hubungan, setHubungan] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(`/keluarga/${id}`).then((r) => setFam(r.data)).catch((e) => toast.error(errMsg(e))), [id]);

  useEffect(() => {
    load();
    api.get("/master/options").then((r) => setMaster(r.data)).catch(() => {});
  }, [load]);

  const openAdd = async () => {
    try {
      const r = await api.get("/jemaat", { params: { page_size: 100 } });
      const already = new Set((fam?.anggota || []).map((a) => a.id));
      setCandidates(r.data.items.filter((j) => !j.keluarga_id || j.keluarga_id === id).filter((j) => !already.has(j.id)));
      setPickJemaat(""); setHubungan("");
      setAddOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const addMember = async () => {
    if (!pickJemaat || !hubungan) { toast.error("Pilih jemaat dan hubungan keluarga"); return; }
    setSaving(true);
    try {
      await api.post(`/keluarga/${id}/anggota`, { jemaat_id: pickJemaat, hubungan_keluarga: hubungan, set_as_kepala: hubungan === "Kepala Keluarga" });
      toast.success("Anggota ditambahkan");
      setAddOpen(false);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const removeMember = async (jemaatId) => {
    try {
      await api.delete(`/keluarga/${id}/anggota/${jemaatId}`);
      toast.success("Anggota dikeluarkan dari keluarga");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!fam) return <div className="text-center py-16 text-slate-400">Memuat...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <KartuKeluargaPrint fam={fam} nonJemaat={nonJemaat} />
      <div className="no-print space-y-5">
      <Breadcrumb items={[{ label: "Data Keluarga" }, { label: "Kartu Keluarga", to: "/keluarga" }, { label: "Detail" }]} />
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" className="rounded-xl" onClick={() => navigate("/keluarga")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Detail Kartu Keluarga</h1>
      </div>

      <Card className="p-5 sm:p-6 rounded-2xl border-slate-200/80 shadow-sm" data-testid="keluarga-hero">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="h-14 w-14 rounded-2xl bg-[hsl(224,64%,33%)]/10 flex items-center justify-center text-[hsl(224,64%,33%)]">
              <Home className="h-7 w-7" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="keluarga-nama">{fam.nama_keluarga}</h2>
              {fam.nomor_kk && <p className="font-mono text-sm text-slate-500 mt-0.5">No. KK: {fam.nomor_kk}</p>}
              {fam.kepala_keluarga && (
                <p className="text-sm text-slate-600 mt-1 flex items-center gap-1.5">
                  <Crown className="h-4 w-4 text-amber-500" /> Kepala Keluarga: <span className="font-medium">{namaGelar(fam.kepala_keluarga)}</span>
                </p>
              )}
              {fam.alamat && <p className="text-sm text-slate-400 mt-1">{fam.alamat}</p>}
              {fam.household && <button type="button" onClick={() => navigate("/household")} className="text-sm text-[hsl(224,64%,33%)] mt-1 flex items-center gap-1.5 hover:underline" data-testid="keluarga-household-link"><Building2 className="h-4 w-4" /> Satu Atap: <span className="font-medium">{fam.household.nama}</span></button>}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-xl" onClick={() => window.print()} data-testid="print-keluarga-btn"><Printer className="h-4 w-4 mr-2" /> Cetak</Button>
          <Button asChild variant="outline" className="rounded-xl border-[hsl(224,64%,33%)]/40 text-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,33%)]/5"><a href={`${API}/keluarga/${id}/pdf?token=${encodeURIComponent(getToken() || "")}`} target="_blank" rel="noreferrer" data-testid="print-pdf-keluarga-btn"><FileDown className="h-4 w-4 mr-2" /> Cetak PDF</a></Button>
          {canWrite && <Button variant="outline" className="rounded-xl" onClick={() => navigate(`/keluarga/${id}/edit`)} data-testid="edit-keluarga-btn">
            <Pencil className="h-4 w-4 mr-2" /> Edit
          </Button>}
          {canDelete && <Button variant="outline" className="rounded-xl text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDelOpen(true)} data-testid="delete-keluarga-btn"><Trash2 className="h-4 w-4 mr-2" /> Hapus</Button>}
          </div>
        </div>
      </Card>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="rounded-2xl" data-testid="delete-keluarga-dialog">
          <DialogHeader><DialogTitle>Hapus Kartu Keluarga?</DialogTitle><DialogDescription>Keluarga <b>{fam.nama_keluarga}</b> akan dinonaktifkan dan {fam.anggota?.length || 0} anggota dilepas dari keluarga ini (data jemaat tidak dihapus). Admin dapat memulihkannya kembali.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" className="rounded-xl" onClick={() => setDelOpen(false)}>Batal</Button><Button className="rounded-xl bg-red-600 hover:bg-red-700" onClick={doDelete} disabled={saving} data-testid="delete-keluarga-confirm">{saving ? "Menghapus..." : "Hapus Keluarga"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">Anggota Keluarga ({fam.anggota?.length || 0})</h3>
        {canWrite && <Button className="rounded-xl bg-[hsl(224,64%,33%)] hover:bg-[hsl(224,64%,28%)]" onClick={openAdd} data-testid="add-anggota-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Tambah Anggota
        </Button>}
      </div>
      <SealamatTanpaKK keluargaId={id} canWrite={canWrite} onAdded={load} />

      {(!fam.anggota || fam.anggota.length === 0) ? (
        <Card className="p-10 text-center rounded-2xl" data-testid="empty-anggota">
          <User className="h-10 w-10 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">Belum ada anggota. Tambahkan jemaat ke keluarga ini.</p>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="anggota-list">
          {fam.anggota.map((a) => (
            <Card key={a.id} data-testid={`anggota-${a.id}`} className="p-4 rounded-2xl flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0 cursor-pointer" onClick={() => navigate(`/jemaat/${a.id}`)}>
                <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400 shrink-0">
                  <User className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                    {a.nama_lengkap}
                    {a.hubungan_keluarga === "Kepala Keluarga" && <Crown className="h-3.5 w-3.5 text-amber-500" />}
                  </div>
                  <div className="font-mono text-xs text-slate-500">NIJ: {a.nij}</div>
                  {(a.ayah_dalam_kk || a.ibu_dalam_kk || a.anak_dalam_kk?.length > 0) && (
                    <div className="text-xs text-slate-500 mt-0.5" data-testid={`relasi-${a.id}`}>
                      {a.ayah_dalam_kk && <span>Ayah: {a.sensitif_disamarkan ? "****" : fam.anggota.find((x) => x.id === a.ayah_id)?.nama_lengkap}</span>}
                      {a.ayah_dalam_kk && a.ibu_dalam_kk && <span> · </span>}
                      {a.ibu_dalam_kk && <span>Ibu: {a.sensitif_disamarkan ? "****" : fam.anggota.find((x) => x.id === a.ibu_id)?.nama_lengkap}</span>}
                      {a.anak_dalam_kk?.length > 0 && <span>{(a.ayah_dalam_kk || a.ibu_dalam_kk) ? " · " : ""}Anak: {a.anak_dalam_kk.map((cid) => fam.anggota.find((x) => x.id === cid)?.nama_lengkap).filter(Boolean).join(", ")}</span>}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {a.hubungan_keluarga && <span className="text-xs px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold hidden sm:inline">{a.hubungan_keluarga}</span>}
                <StatusBadge value={a.status_jemaat} type="jemaat" className="hidden sm:inline-flex" />
                {canWrite && <Button variant="ghost" size="icon" className="rounded-xl text-red-500 hover:bg-red-50" onClick={() => removeMember(a.id)} data-testid={`remove-anggota-${a.id}`}>
                  <X className="h-4 w-4" />
                </Button>}
              </div>
            </Card>
          ))}
        </div>
      )}

      <AnggotaNonJemaat keluargaId={id} canWrite={canWrite} onChange={setNonJemaat} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle>Tambah Anggota Keluarga</DialogTitle>
            <DialogDescription>Pilih jemaat dan tentukan hubungannya dalam keluarga.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Pilih Jemaat</Label>
              <SearchableSelect
                options={candidates.map((c) => ({ value: c.id, label: `${namaGelar(c)} (${c.nij})` }))}
                value={candidates.find((c) => c.id === pickJemaat) ? `${namaGelar(candidates.find((c) => c.id === pickJemaat))} (${candidates.find((c) => c.id === pickJemaat).nij})` : ""}
                onChange={setPickJemaat}
                placeholder="Cari jemaat..."
                testId="pick-jemaat-select"
                allowClear={false}
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Hubungan Keluarga</Label>
              <SearchableSelect options={master.hubungan_keluarga} value={hubungan} onChange={setHubungan} placeholder="Pilih hubungan" testId="hubungan-select" allowClear={false} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setAddOpen(false)}>Batal</Button>
            <Button className="rounded-xl bg-[hsl(224,64%,33%)]" onClick={addMember} disabled={saving} data-testid="confirm-add-anggota-btn">
              {saving ? "Menyimpan..." : "Tambahkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
